import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { DataTable } from '../../components/Tables';
import PageHeader from '../shared/PageHeader';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { judgesApi, extractList } from '../../api/facades';

const JudgesListPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [isActive, setIsActive] = useState<'Yes' | 'No' | ''>('');
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { data: res, isLoading } = useQuery({
    queryKey: ['judges', search, isActive],
    queryFn: () => judgesApi.list({ search: search || undefined, isActive: isActive || undefined }),
  });
  const rows = extractList(res);

  const deleteMu = useMutation({
    mutationFn: (id: number) => judgesApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['judges'] });
      setDeleteId(null);
      toast.success('Judge deleted.');
    },
    onError: (e: any) => toast.error(e?.message || 'Delete failed.'),
  });

  const columns = [
    { key: 'judge_name', label: 'Name' },
    { key: 'is_active', label: 'Active', render: (r: any) => (r.is_active === 'Yes' ? 'Yes' : 'No') },
  ];

  const judgesBase = '/dashboard/settings/judges';

  return (
    <div>
      <PageHeader hideTitle subtitle="Manage judges." primaryAction={{ label: 'New Judge', to: `${judgesBase}/new` }} />
      <div className="bg-white dark:bg-slate-800/50 rounded-2xl p-6 border border-gray-100 dark:border-gray-700">
        <div className="mb-4 flex flex-wrap gap-4">
          <select value={isActive} onChange={(e) => setIsActive(e.target.value as 'Yes' | 'No' | '')} className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm">
            <option value="">All</option>
            <option value="Yes">Active</option>
            <option value="No">Inactive</option>
          </select>
          <input type="search" placeholder="Search name" value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-lg border border-gray-300 bg-white dark:bg-slate-800 dark:border-gray-600 py-2 px-3 text-sm w-48" />
        </div>
        <DataTable columns={columns} data={rows} loading={isLoading} emptyMessage="No judges." onRowClick={(id) => navigate(`${judgesBase}/${id}`)} onEdit={(id) => navigate(`${judgesBase}/${id}/edit`)} onDelete={(id) => setDeleteId(Number(id))} />
      </div>
      <ConfirmDeleteModal open={deleteId != null} onClose={() => setDeleteId(null)} onConfirm={() => deleteId != null && deleteMu.mutate(deleteId)} title="Delete judge" message="Are you sure? This cannot be undone." loading={deleteMu.isPending} />
    </div>
  );
};

export default JudgesListPage;
